/**
 * Elevation's own search (Cmd/Ctrl+K): the owner's, on the owner's pages.
 *
 * It searches Elevation's registry, templates, examples, glossary and notes
 * (lib/unifiedSearch.ts), so it is no part of the product (Proposal 09 § 4,
 * coupling 4). The product's search is its command palette
 * (components/prioritization/CommandPalette.tsx), on product data only.
 *
 * - Loaded on demand, for the owner alone. For anyone else the search, and the
 *   Elevation modules behind it, are never fetched or run. Imported statically,
 *   they ran at start-up for everyone, and collaborationTracker wrote
 *   Elevation's sample team stories into every browser's localStorage.
 * - Cmd/Ctrl+K opens it on the owner's own pages (ELEVATION_OWNER_ONLY_PATHS),
 *   where the Elevation header and its Search button are drawn. On every other
 *   page the key is left to the product.
 */
import { lazy, Suspense, useEffect } from 'react';
import { isElevationOwnerOnlyPath, useIsElevationOwner } from '../lib/elevationAccess';

// reuse-ack: mirrors src/components/UnifiedSearchModal.tsx — not a copy: React.lazy's handle to that same component, so it loads on demand
const UnifiedSearchModal = lazy(() =>
  import('./UnifiedSearchModal').then((m) => ({ default: m.UnifiedSearchModal })),
);

interface ElevationSearchProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ElevationSearch({ isOpen, onOpenChange }: ElevationSearchProps) {
  const owner = useIsElevationOwner();

  useEffect(() => {
    if (!owner) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return;
      if (!isElevationOwnerOnlyPath(window.location.pathname)) return;
      e.preventDefault();
      onOpenChange(true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [owner, onOpenChange]);

  if (!owner) return null;
  return (
    <Suspense fallback={null}>
      <UnifiedSearchModal isOpen={isOpen} onClose={() => onOpenChange(false)} />
    </Suspense>
  );
}
